// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import requestQuote from "@flip/App/Handlers/RequestQuote/RequestQuoteHandler";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { xIsAuthenticated } from "@flip/App/Store/Selectors/GeneralSelectors";
import { initialQuoteFormState, QuoteFormState } from "@flip/App/Widgets/RequestQuote/RequestQuoteForm.module";
import FileUpload, { FileUploadPresentationProps } from "@flip/MobiusOverrides/FileUpload/FileUpload";
import TextField from "@flip/MobiusOverrides/TextField";
import ReCaptcha from "@flip/Overrides/Components/ReCaptcha";
import { isEmptyOrUndefined, xMask, xScreen } from "@flip/XDL/Utilities";
import { CreateForm, FormField } from "@flip/XDL/Utilities/form";
import { xValidateOnSubmit } from "@flip/XDL/Utilities/xValidate";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TextArea } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { useContext, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
    requestQuote,
};

const mapStateToProps = (state: ApplicationState & CustomAppState) => {
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        isAuthenticated: xIsAuthenticated(state),
        session: state.context.session,
        currentBillTo: getCurrentBillToState(state).value,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestQuoteFormStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    halfWidthGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
    fileUploader?: FileUploadPresentationProps;
    uploadContainer?: InjectableCss;
    // XNG-changes
    emailGridItem?: GridItemProps;
    emailError?: TypographyPresentationProps;
}

export const requestQuoteFormStyles: RequestQuoteFormStyles = {
    form: {
        css: css`
            margin-top: 22px;
            max-width: 840px;
            ${xScreen("lte", "sm")} {
                width: 100%;
                max-width: 100%;
            }
        `,
    },
    container: {
        gap: 27,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: {
        width: 12,
    },
    submitGridItem: {
        width: 12,
    },
    halfWidthGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    stateGridItem: {
        width: [6, 6, 3, 3, 3],
    },
    uploadContainer: {
        css: css`
            width: 100%;
            [class^="ButtonWrapper"] {
                margin-top: auto;
            }
        `,
    },
};

const styles = requestQuoteFormStyles;
const StyledForm = getStyledWrapper("form");
const formId = "requestQuoteForm";

const RequestQuoteForm = ({ validateReCaptcha, requestQuote, session, isAuthenticated, currentBillTo }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isDisallowedEmail, setIsDisallowedEmail] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const formRef = useRef<HTMLFormElement>(null);
    const [quoteFormState, setQuoteFormState] = useState<QuoteFormState>(cloneDeep(initialQuoteFormState));
    const quoteForm = new CreateForm<QuoteFormState>(quoteFormState, setQuoteFormState, {
        session,
        isAuthenticated,
        currentBillTo,
    });
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();

    const fields = quoteForm?.state.fields;

    useEffect(() => {
        if (isAuthenticated) {
            !isEmptyOrUndefined(session.firstName) && quoteForm?.onFieldChange(fields.firstName, session.firstName);
            !isEmptyOrUndefined(session.lastName) && quoteForm?.onFieldChange(fields.lastName, session.lastName);
            !isEmptyOrUndefined(session.email) && quoteForm?.onFieldChange(fields.email, session.email);
            !isEmptyOrUndefined(currentBillTo?.companyName) &&
                quoteForm?.onFieldChange(fields.company, currentBillTo?.companyName);
            !isEmptyOrUndefined(currentBillTo?.phone) && quoteForm?.onFieldChange(fields.phone, currentBillTo?.phone);
        }
    }, [session, currentBillTo, isAuthenticated]);

    const buildFormParamsFromState = (): FormData => {
        const formData = new FormData();
        const { fields } = quoteForm.state;
        for (const field in fields) {
            const data: FormField<string> = fields[field as keyof typeof fields];

            if (data.value && !data.error) {
                formData.append(field, data.value);
            }
        }
        return formData;
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsDisallowedEmail(false);
        setIsSubmitting(false);
        toasterContext.addToast({ body: siteMessage("Flip_Custom_Quote_Submit_Success"), messageType: "success" });
        quoteForm.clearState();
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const errors = xValidateOnSubmit(formId);

        if (errors.length > 0 || !fields) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "XNG-RequestQuoteForm",
            recaptchaRef,
        });
        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);

        const formData: FormData = buildFormParamsFromState();
        if (!formData) {
            return;
        }

        if (!quoteForm.state.fields.file.value) {
            formData.delete(quoteForm.state.fields.file.attrs!.name!);
        }

        requestQuote({
            parameters: formData,
            onSuccess: onSuccessHandler,
            onError: (error: any) => {
                // XNG-Changes
                let errorData: any = error;
                if (typeof error === "string") {
                    try {
                        errorData = JSON.parse(error);
                    } catch (e) {
                        errorData = { message: error };
                    }
                }
                const message = errorData?.message || error;
                const isDisallowed = errorData?.isDisallowedEmail || false;

                setErrorMessage(message);
                setIsDisallowedEmail(isDisallowed);
                setIsSubmitting(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        });
    };

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate ref={formRef}>
            <GridContainer {...styles.container}>
                {/** XNG-Changes: FLIP-992 */}
                <GridItem width={12} style={{ justifyContent: "flex-end" }}>
                    <Typography variant="legend">{translate("*Required")}</Typography>
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={
                            isAuthenticated && !isEmptyOrUndefined(session.firstName)
                                ? session.firstName
                                : fields.firstName.value
                        }
                        onChange={e => quoteForm?.onFieldChange(fields.firstName, e.currentTarget.value)}
                        error={fields.firstName.error}
                        {...fields.firstName.attrs}
                        disabled={isAuthenticated && !isEmptyOrUndefined(session.firstName)}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={
                            isAuthenticated && !isEmptyOrUndefined(session.lastName)
                                ? session.lastName
                                : fields.lastName.value
                        }
                        onChange={e => quoteForm?.onFieldChange(fields.lastName, e.currentTarget.value)}
                        error={fields.lastName.error}
                        {...fields.lastName.attrs}
                        disabled={isAuthenticated && !isEmptyOrUndefined(session.lastName)}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.emailGridItem}>
                    <TextField
                        value={
                            isAuthenticated && !isEmptyOrUndefined(session.lastName)
                                ? session.email
                                : fields.email.value
                        }
                        onChange={e => {
                            quoteForm?.onFieldChange(fields.email, e.currentTarget.value);
                            // XNG-changes
                            if (isDisallowedEmail) {
                                setIsDisallowedEmail(false);
                                setErrorMessage("");
                            }
                        }}
                        error={fields.email.error || (isDisallowedEmail ? " " : undefined)}
                        {...fields.email.attrs}
                        disabled={isAuthenticated && !isEmptyOrUndefined(session.lastName)}
                        {...{ formId }}
                    />
                    {isDisallowedEmail && <Typography {...styles.emailError}>{errorMessage}</Typography>}
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={
                            isAuthenticated && !isEmptyOrUndefined(currentBillTo?.companyName)
                                ? currentBillTo?.companyName
                                : fields.company.value
                        }
                        onChange={e => quoteForm?.onFieldChange(fields.company, e.currentTarget.value)}
                        error={fields.company.error}
                        {...fields.company.attrs}
                        disabled={isAuthenticated && !isEmptyOrUndefined(currentBillTo?.companyName)}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={xMask.qty(fields.quantity.value)}
                        onChange={e => quoteForm?.onFieldChange(fields.quantity, e.currentTarget.value)}
                        error={fields.quantity.error}
                        {...fields.quantity.attrs}
                        maxLength={6}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={xMask.fourDecimalNumber(fields.targetPrice.value)}
                        onChange={e => quoteForm?.onFieldChange(fields.targetPrice, e.currentTarget.value)}
                        error={fields.targetPrice.error}
                        {...fields.targetPrice.attrs}
                        maxLength={12}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.manufacturer.value}
                        onChange={e => quoteForm?.onFieldChange(fields.manufacturer, e.currentTarget.value)}
                        error={fields.manufacturer.error}
                        {...fields.manufacturer.attrs}
                        {...{ formId }}
                    />
                </GridItem>

                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={fields.partNumber.value}
                        onChange={e => quoteForm?.onFieldChange(fields.partNumber, e.currentTarget.value)}
                        error={fields.partNumber.error}
                        {...fields.partNumber.attrs}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <TextField
                        value={
                            isAuthenticated && !isEmptyOrUndefined(currentBillTo?.phone)
                                ? currentBillTo?.phone
                                : fields.phone.value
                        }
                        onChange={e => quoteForm?.onFieldChange(fields.phone, e.currentTarget.value)}
                        error={fields.phone.error}
                        {...fields.phone.attrs}
                        disabled={isAuthenticated && !isEmptyOrUndefined(currentBillTo?.phone)}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.halfWidthGridItem}>
                    <StyledWrapper {...styles.uploadContainer}>
                        <FileUpload
                            fileName={fields.file?.value?.name || ""}
                            onFileChange={e => quoteForm?.onFieldChange(fields.file, e.target.files?.[0])}
                            error={fields.file.error}
                            labelPosition="top"
                            accept=".xls,.xlsx,.csv,.txt,.pdf"
                            uid="FlipRequestQuoteFileUpload"
                            {...fields.file.attrs}
                            {...{ formId }}
                        />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.submitErrorGridItem}>
                    <TextArea
                        value={fields.message.value}
                        onChange={e => quoteForm?.onFieldChange(fields.message, e.currentTarget.value)}
                        error={fields.message.error}
                        rows={3}
                        {...fields.message.attrs}
                    />
                </GridItem>
                {errorMessage && !isDisallowedEmail && (
                    <GridItem data-test-selector="requestQuote_ErrorMessage" {...styles.submitErrorGridItem}>
                        <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                    </GridItem>
                )}
                <GridItem {...styles.reCaptchaGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="XNG-RequestQuoteForm" />
                </GridItem>
                <GridItem {...styles.submitGridItem}>
                    <Button
                        {...styles.submitButton}
                        type="submit"
                        disabled={isSubmitting}
                        data-test-selector="requestQuote_createButton"
                    >
                        {translate("Request A Quote")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestQuoteForm),
    definition: {
        group: "Request Quote" as any,
    },
};

export default widgetModule;
